import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { USERS } from '../mock/data';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);
axios.defaults.withCredentials = true; // send Emergent session cookie

const REFRESH_EVERY_MS = 60 * 60 * 1000;
const isAuthCall = (url = '') => /\/auth\/(login|register|google\/session|forgot-password|reset-password|logout)/.test(url);

function clearStoredSession() {
  localStorage.removeItem('kmt_token');
  localStorage.removeItem('kmt_user');
  delete axios.defaults.headers.common['Authorization'];
}

// Exchange the current token for a fresh one (sliding session) so active users stay signed in.
async function refreshToken() {
  if (!localStorage.getItem('kmt_token')) return;
  try {
    const { data } = await axios.post(`${API}/auth/refresh`, {});
    localStorage.setItem('kmt_token', data.access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
  } catch (e) { /* 401 is handled by the interceptor */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const setAuthHeader = (token) => {
    if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    else delete axios.defaults.headers.common['Authorization'];
  };

  // Expired/invalid session anywhere in the app -> sign out cleanly instead of failing every action.
  useEffect(() => {
    const id = axios.interceptors.response.use((r) => r, (err) => {
      const url = err?.config?.url || '';
      if (err?.response?.status === 401 && !isAuthCall(url) && !url.includes('/auth/me') && localStorage.getItem('kmt_user')) {
        clearStoredSession();
        setUser(null);
        toast.error('Session expired — please sign in again', { id: 'session-expired' });
        if (!window.location.pathname.startsWith('/login')) window.location.assign('/login');
      }
      return Promise.reject(err);
    });
    return () => axios.interceptors.response.eject(id);
  }, []);

  // Keep the JWT fresh while the app is open or when it comes back to the foreground.
  useEffect(() => {
    if (!user) return undefined;
    refreshToken();
    const t = setInterval(refreshToken, REFRESH_EVERY_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') refreshToken(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVisible); };
  }, [user?.id]);

  useEffect(() => {
    // If returning from Google OAuth, let AuthCallback exchange the session first.
    if (window.location.hash?.includes('session_id=')) { setLoading(false); return; }
    const token = localStorage.getItem('kmt_token');
    const saved = localStorage.getItem('kmt_user');
    if (token) { setAuthHeader(token); if (saved) { try { setUser(JSON.parse(saved)); } catch (e) {} } }
    // Always verify with /me — covers both JWT (Bearer) and Emergent cookie sessions
    axios.get(`${API}/auth/me`)
      .then((res) => {
        setUser(res.data);
        if (token) localStorage.setItem('kmt_user', JSON.stringify(res.data));
      })
      .catch(() => {
        localStorage.removeItem('kmt_token');
        localStorage.removeItem('kmt_user');
        setAuthHeader(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: u } = res.data;
      localStorage.setItem('kmt_token', access_token);
      localStorage.setItem('kmt_user', JSON.stringify(u));
      setAuthHeader(access_token);
      setUser(u);
      return { ok: true };
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Invalid email or password';
      return { ok: false, error: msg };
    }
  };

  const quickLogin = async () => ({ ok: false, error: 'Quick login disabled' });

  const register = async (name, email, password) => {
    try {
      await axios.post(`${API}/auth/register`, { name, email, password });
      // New accounts start as PENDING (no access) until an admin assigns a role.
      return { ok: true, pending: true };
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Registration failed';
      return { ok: false, error: msg };
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}); } catch (e) { console.warn('Logout request failed (clearing session anyway):', e?.message || e); }
    localStorage.removeItem('kmt_token');
    localStorage.removeItem('kmt_user');
    setAuthHeader(null);
    setUser(null);
  };

  const completeGoogleSession = async (sessionId) => {
    const res = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
    setUser(res.data);
    return { ok: true, user: res.data };
  };

  const value = useMemo(
    () => ({ user, loading, login, quickLogin, register, logout, completeGoogleSession }),
    [user, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// re-export for other components that referenced seeded users
export { USERS };
